import { useDispatch, useSelector } from 'react-redux';
import { getIsDarkModeOn } from '@stores/selectors/darkModeStatusSelectors';
import useDarkModeStatus from '@stores/useDarkModeStatus';
import styled from '@emotion/styled';
import Button from '@base/Button/Button';
import Paper from '@base/Paper/Paper';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import React from 'react';
import Tags from '@base/Tags/Tags';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import { finishPracticeSession } from '@actions/practiceSession';
import { PRACTICE_STARTED } from '@constants/segment';
import useFinishSummary from '@stores/auto-dpp/dppFinishSummaryStore';
import { emptyObject } from '@constants/empty';
import usePracticeStore from '@stores/planner/practiceStore';
import usePrimeTrialPromoModalStore from '@stores/prime-pitch/store';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import { getIsSubscribedToGoal } from '@selectors/subscription';
import usePrimeQuotaStore from '@stores/prime-quota/store';
import { PrimeMode } from '@stores/prime-quota/types';
import { readCookie } from '@utils/cookies';
import { openToggleableElement } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import AvatarGroup from './AvatarGroup';
import EducatorAvatar from './EducatorAvatar';
import {
  DPP_COMPLETED,
  DPP_INCOMPLETE,
  PRACTICE_FLOW_TYPE,
  PRACTICE_TYPE
} from '../constants';
import usePracticeSegmentAnalytics from '../usePracticeSegmentAnalytics';

const OuterWrapper = styled(Paper)`
  background-color: var(--color-base-1);
  width: 416px;
  display: flex;
  margin-right: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_16B};
  flex-direction: column;
  justify-content: space-between;

  ${mediaQuery.uptoMobile} {
    width: calc(100vw - 48px);
    max-width: 416px;
    margin-right: 0;
    margin-bottom: ${Spacings.SPACING_4B};
  }
`;

const TopicGroupTag = styled(Tags)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  padding: 0;
  width: 100%;
  line-height: ${Spacings.SPACING_3B};
  margin-bottom: ${Spacings.SPACING_2};
  max-width: none;
  color: ${({ color }) => color};
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.EXTRA_SMALL};
  }
`;

const StyledButton = styled(Button)`
  border-radius: ${Spacings.SPACING_1B};
  font-size: ${Fonts.SMALL};
  height: ${Spacings.SPACING_10B};
  color: ${({ $isDarkModeOn, $textColor }) =>
    $isDarkModeOn ? $textColor : ''};
`;

const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_14B};
  margin-top: ${Spacings.SPACING_4B};
  border-top: solid 1px var(--color-divider);
`;

const StyledTitle = styled.div`
  font-size: ${Fonts.LARGE};
  font-weight: ${Fonts.SEMIBOLD};
  max-width: 280px;
  line-height: ${Spacings.SPACING_5B};
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: ${Spacings.SPACING_2};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  color: var(--color-text-primary);
`;

const StyledDetail = styled.div`
  margin-top: ${({ $marginTop }) => $marginTop};
  margin-bottom: ${({ $marginBottom }) => $marginBottom};
  color: var(--color-text-secondary);
  font-size: ${Fonts.ULTRA_SMALL};
  line-height: ${Spacings.SPACING_18};
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InfoWrapper = styled.div`
  height: 86px;
`;

const CountInfo = styled.div`
  display: flex;
  align-items: center;
`;

const PracticeCard = ({ practice }) => {
  const {
    classDetails,
    educatorDetails,
    sessionDetails,
    numQuestions,
    totalQuizAttempts,
    uid: quizUID
  } = practice ?? emptyObject;

  const router = useRouter();
  const { goalUID, goalSlug } = router.query;

  const isDarkMode = useDarkModeStatus(getIsDarkModeOn);
  const dispatch = useDispatch();
  const { topicGroupName, className, topicGroupUid, topicColor } = classDetails;
  const { firstName, lastName, avatar } = educatorDetails;
  const { toggleShowSummary, setFinishSummary } = useFinishSummary();
  const { finishPlannerPracticeSession } = usePracticeStore();
  const segmentAnalytics = usePracticeSegmentAnalytics(goalUID);
  const { solvedQuestions, status, sessionUid } = sessionDetails;

  const isCompleted = status === DPP_COMPLETED;
  const isInComplete = status === DPP_INCOMPLETE;
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const isSubscribed = useSelector(getIsSubscribedToGoal(goalUID));
  const openPrimeTrialPromoModal = usePrimeTrialPromoModalStore(
    (state) => state.openModal
  );
  const consumeQuota = usePrimeQuotaStore((state) => state.consumeQuota);
  const getQuotaForMode = usePrimeQuotaStore((state) => state.getQuotaForMode);

  const getBtnLabel = () => {
    if (isCompleted) {
      return 'View';
    }
    if (isInComplete && sessionUid && solvedQuestions >= 1) {
      return 'Resume';
    }
    return 'Start';
  };

  const getBtnVariant = () => {
    if (isInComplete && solvedQuestions === 0) {
      return 'filled';
    }
    return 'hollow';
  };

  const getTextColorForDarkMode = () => {
    if (isInComplete && solvedQuestions === 0) {
      return 'var(--color-i-black)';
    }
    return 'var(--color-i-gray-9)';
  };

  const handleButtonClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if user is logged in
    if (!isLoggedIn) {
      openToggleableElement(LOGIN_DRAWER);
      return;
    }

    if (isCompleted && sessionUid) {
      finishPlannerPracticeSession({}, false);
      await dispatch(finishPracticeSession(sessionUid));
      setFinishSummary({
        goalSlug,
        goalUID,
        sessionId: sessionUid,
        topicGroup: topicGroupUid,
        topicUID: topicGroupUid,
        quizUID,
        type: PRACTICE_FLOW_TYPE.AUTO_DPP,
        educatorName: `${firstName} ${lastName}`
      });
      return toggleShowSummary();
    }

    // Check and consume quota for free users before starting practice
    if (!isSubscribed && !isCompleted) {
      const currentQuota = getQuotaForMode(PrimeMode.PRACTICE);

      // Check if quota is exhausted before consuming
      if (currentQuota.isQuotaExhausted) {
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.QUESTION_QUOTA,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: `/goal/${goalSlug}/${goalUID}/practice`
          }
        );
        return;
      }

      // Consume quota if available
      const accessToken = readCookie('accessToken');
      await consumeQuota({
        goalUID,
        mode: PrimeMode.PRACTICE,
        accessToken
      });
    }

    if (quizUID && topicGroupUid && goalUID && goalSlug) {
      return pushRoute({
        pathname: '/goal/[goalSlug]/[goalUID]/practice/[topicUID]',
        query: {
          goalSlug,
          goalUID,
          topic_type: PRACTICE_TYPE.EACH_TOPIC,
          type: PRACTICE_FLOW_TYPE.AUTO_DPP,
          topic_group: topicGroupUid,
          topicUID: topicGroupUid,
          quiz_uid: quizUID,
          educator_name: `${firstName} ${lastName}`,
          source: 'Practice'
        }
      }).then(() =>
        segmentAnalytics(PRACTICE_STARTED, {
          topic_uid: null,
          practice_questions: numQuestions,
          topology_id: topicGroupUid,
          topology_name: 'NA',
          topology_level: 'NA',
          last_primary_source: 'Practice',
          language: 'NA',
          practice_type: 'Educator Led Practice',
          is_completed: isCompleted ? 'Yes' : 'No'
        })
      );
    }
  };

  return (
    <OuterWrapper padding={Spacings.SPACING_6B}>
      <Flex>
        <InfoWrapper>
          <TopicGroupTag type="transparent" color={topicColor}>
            {topicGroupName}
          </TopicGroupTag>
          <StyledTitle>{className}</StyledTitle>
          <StyledDetail
            $marginTop={Spacings.SPACING_1B}
            $marginBottom={Spacings.SPACING_2}
          >
            {firstName} {lastName}
          </StyledDetail>
          <StyledDetail $marginTop={Spacings.SPACING_1B}>
            {solvedQuestions} of {numQuestions} attempted
          </StyledDetail>
        </InfoWrapper>
        <EducatorAvatar avatar={avatar} circleColor={topicColor} />
      </Flex>

      <ActionWrapper>
        <CountInfo>
          <AvatarGroup />
          <StyledDetail>{totalQuizAttempts}</StyledDetail>
        </CountInfo>

        <StyledButton
          $isDarkModeOn={isDarkMode}
          variant={getBtnVariant()}
          color="black"
          label={getBtnLabel()}
          $textColor={getTextColorForDarkMode()}
          width="120px"
          maxHeight="sm"
          onClick={handleButtonClick}
        />
      </ActionWrapper>
    </OuterWrapper>
  );
};

export default PracticeCard;
